import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { House, BookOpen, Timer, Settings, Info as InfoIcon, LogOut } from "lucide-react";
import { ContactFooter } from "@/components/ContactFooter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const baseNavItems = [
  { to: "/", label: "Home", icon: House, testid: "nav-home" },
  { to: "/moduli", label: "Moduli", icon: BookOpen, testid: "nav-moduli" },
  { to: "/simulazioni", label: "Allénati", icon: Timer, testid: "nav-simulazioni" },
];
const adminNavItem = { to: "/admin", label: "Gestione", icon: Settings, testid: "nav-admin" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const navItems = user?.is_admin ? [...baseNavItems, adminNavItem] : baseNavItems;

  return (
    <div className="min-h-screen bg-white">
      {/* Skip to content — keyboard users */}
      <a href="#main-content" className="skip-link" data-testid="skip-to-content-link">
        Salta al contenuto
      </a>

      {/* Top app bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-[color:var(--hairline)]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[color:var(--volt)] border border-black/10" />
            <span className="font-extrabold tracking-tight text-[15px] uppercase">Propedeutico</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Navigazione principale">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testid}-desktop`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-[color:var(--ink)] text-white" : "text-[color:var(--ink)] hover:bg-[color:var(--bg-subtle)]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-user-menu-button"
                className="w-9 h-9 rounded-full bg-[color:var(--ink)] text-white font-extrabold text-sm flex items-center justify-center hover:bg-black/85 transition-colors duration-200"
                aria-label="Menu utente"
              >
                {(user?.nome || user?.email || "U").charAt(0).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-3 py-2">
                <p className="text-sm font-bold truncate">{user?.nome}</p>
                <p className="text-xs text-[color:var(--text-2)] truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              {user?.is_admin && (
                <DropdownMenuItem data-testid="menu-admin-link" onClick={() => navigate("/admin")}>
                  <Settings className="w-4 h-4 mr-2" /> Gestione contenuti
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="menu-info-link" onClick={() => navigate("/info")}>
                <InfoIcon className="w-4 h-4 mr-2" /> Info e note legali
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-logout-button"
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
              >
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Page content — animated per route, respects reduced motion */}
      <main id="main-content" className="max-w-3xl mx-auto px-4 pb-28 md:pb-16">
        <motion.div
          key={location.pathname}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <Outlet />
        </motion.div>
        <footer className="mt-12 pt-6 border-t border-[color:var(--hairline)]">
          <ContactFooter className="mt-0 mb-5" />
          <nav aria-label="Link legali" className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <a
              href="https://misteracademy.it/termini.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-terms-link"
              className="text-xs font-semibold text-[color:var(--text-2)] underline underline-offset-2 hover:text-[color:var(--ink)] transition-colors duration-200"
            >
              Termini &amp; Condizioni
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/36827680"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-privacy-link"
              className="text-xs font-semibold text-[color:var(--text-2)] underline underline-offset-2 hover:text-[color:var(--ink)] transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/36827680/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cookie-link"
              className="text-xs font-semibold text-[color:var(--text-2)] underline underline-offset-2 hover:text-[color:var(--ink)] transition-colors duration-200"
            >
              Cookie Policy
            </a>
          </nav>
          <p className="text-xs text-[color:var(--text-2)] leading-5">
            App di supporto alla preparazione del test di idoneità. Non è un prodotto ufficiale FIGC.{" "}
            <Link to="/info" className="underline underline-offset-2" data-testid="footer-info-link">
              Maggiori info
            </Link>
          </p>
        </footer>
      </main>

      {/* Bottom nav — mobile */}
      <nav aria-label="Navigazione principale mobile" className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/92 backdrop-blur border-t border-[color:var(--hairline)]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className={`grid ${user?.is_admin ? "grid-cols-4" : "grid-cols-3"}`}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] text-[11px] font-semibold transition-colors duration-200 ${
                    isActive ? "text-[color:var(--ink)]" : "text-[color:var(--text-2)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`flex items-center justify-center w-10 h-6 rounded-full ${isActive ? "bg-[color:var(--volt)]" : ""}`}>
                      <Icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.5 : 2} />
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
